/**
 * MessageInput — форма отправки сообщений активной комнаты:
 *   - reply-bar / pending file preview
 *   - mention popover (`@…` → matched users из `mentionCandidates`)
 *   - emoji picker (5 категорий + recent, persisted в EMOJI_RECENT_KEY)
 *   - attachment upload через uploadDocumentFile (POST /documents)
 *   - draft persisted в localStorage `chat:draft:<roomId>` per-room
 *
 * Sync: `onTyping(true|false)` дёргает WS-индикатор «печатает»;
 * `onSend(payload)` зовётся на Enter / клик. Live (WS) предупреждение
 * появляется при `liveSyncConnected=false` — REST fallback всё равно
 * работает.
 */

import { useState, useEffect, useRef } from 'react'
import { X, Send, Paperclip, Smile } from 'lucide-react'
import { uploadDocumentFile } from '../../../api/documents'
import {
    EMOJI_CATEGORIES,
    EMOJI_RECENT_KEY,
    QUICK_EMOJIS,
} from '../../../utils/chatHelpers'
import { displayName } from '../../../utils/nameHelpers'

export default function MessageInput({
    onSend,
    onTyping,
    disabled,
    /** Если false — live (WS) нет; отправка всё равно через REST, показываем подсказку */
    liveSyncConnected = true,
    mentionCandidates = [],
    replyTo = null,
    onCancelReply,
    roomId,
}) {
    const [text, setText] = useState('')
    const [showEmoji, setShowEmoji] = useState(false)
    const [emojiTab, setEmojiTab] = useState('recent')
    const [recentEmojis, setRecentEmojis] = useState(() => QUICK_EMOJIS.slice(0, 6))
    const [uploading, setUploading] = useState(false)
    const [mentionOpen, setMentionOpen] = useState(false)
    const [mentionFilter, setMentionFilter] = useState('')
    const [pendingFile, setPendingFile] = useState(null)
    const textareaRef = useRef(null)
    const fileRef = useRef(null)
    const typingTimeout = useRef(null)
    const pickedMentionIdsRef = useRef([])

    useEffect(() => {
        const key = roomId ? `chat:draft:${roomId}` : null
        if (!key) return
        try {
            const saved = localStorage.getItem(key)
            setText(saved || '')
        } catch {
            setText('')
        }
    }, [roomId])

    useEffect(() => {
        const key = roomId ? `chat:draft:${roomId}` : null
        if (!key) return
        try {
            if (text) {
                localStorage.setItem(key, text)
            } else {
                localStorage.removeItem(key)
            }
        } catch {
            // no-op: localStorage can be unavailable in some environments
        }
    }, [roomId, text])

    useEffect(() => {
        try {
            const raw = localStorage.getItem(EMOJI_RECENT_KEY)
            if (!raw) return
            const parsed = JSON.parse(raw)
            if (Array.isArray(parsed) && parsed.length) {
                setRecentEmojis(parsed.filter(Boolean).slice(0, 24))
            }
        } catch {
            // ignore broken localStorage data
        }
    }, [])

    const filteredMentions = mentionOpen
        ? mentionCandidates.filter((u) => {
            const q = (mentionFilter || '').toLowerCase()
            if (!q) return true
            const name = (u.full_name || u.login || '').toLowerCase()
            const login = (u.login || '').toLowerCase()
            return name.includes(q) || login.includes(q)
        }).slice(0, 8)
        : []

    const handleChange = (e) => {
        const v = e.target.value
        setText(v)
        e.target.style.height = 'auto'
        e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'

        const sel = e.target.selectionStart
        const before = v.slice(0, sel)
        const mm = before.match(/@([^@\n]{0,48})$/)
        if (mm && mentionCandidates.length) {
            setMentionOpen(true)
            setMentionFilter(mm[1] || '')
        } else {
            setMentionOpen(false)
            setMentionFilter('')
        }

        onTyping?.(true)
        clearTimeout(typingTimeout.current)
        typingTimeout.current = setTimeout(() => onTyping?.(false), 2000)
    }

    const insertMention = (u) => {
        const ta = textareaRef.current
        if (!ta) return
        const sel = ta.selectionStart
        const v = text
        const before = v.slice(0, sel)
        const after = v.slice(sel)
        const m = before.match(/@([^@\n]{0,48})$/)
        const start = m ? before.length - m[0].length : before.length
        const label = displayName(u.full_name) || u.login || 'user'
        const next = `${v.slice(0, start)}@${label} ${after}`
        setText(next)
        const nid = String(u.id)
        if (!pickedMentionIdsRef.current.includes(nid)) pickedMentionIdsRef.current.push(nid)
        setMentionOpen(false)
        setMentionFilter('')
        requestAnimationFrame(() => {
            const pos = start + label.length + 2
            ta.focus()
            ta.setSelectionRange(pos, pos)
        })
    }

    const handleSend = async () => {
        const trimmed = text.trim()
        if (!trimmed && !pendingFile) return
        const payload = {
            content: trimmed,
            mentionedUserIds: [...pickedMentionIdsRef.current],
            fileUrl: pendingFile?.url || null,
            fileName: pendingFile?.filename || null,
            fileSize: pendingFile?.size ?? null,
            replyToId: replyTo?.id || null,
        }
        await Promise.resolve(onSend?.(payload))
        setText('')
        setPendingFile(null)
        pickedMentionIdsRef.current = []
        onTyping?.(false)
        clearTimeout(typingTimeout.current)
        onCancelReply?.()
        if (textareaRef.current) textareaRef.current.style.height = 'auto'
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !mentionOpen) {
            e.preventDefault()
            void handleSend()
        }
        if (e.key === 'Escape') setMentionOpen(false)
    }

    const onPickFile = async (e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file || disabled) return
        setUploading(true)
        try {
            const data = await uploadDocumentFile(file)
            setPendingFile({
                url: data.url,
                filename: data.filename || file.name,
                size: data.size ?? file.size,
            })
        } catch (err) {
            console.error(err)
        } finally {
            setUploading(false)
        }
    }

    const appendEmoji = (ch) => {
        setText((t) => t + ch)
        setRecentEmojis((prev) => {
            const next = [ch, ...prev.filter((x) => x !== ch)].slice(0, 24)
            try {
                localStorage.setItem(EMOJI_RECENT_KEY, JSON.stringify(next))
            } catch {
                // ignore storage issues
            }
            return next
        })
        setEmojiTab('recent')
        textareaRef.current?.focus()
    }

    const emojiItems = emojiTab === 'recent'
        ? (recentEmojis.length ? recentEmojis : QUICK_EMOJIS)
        : (EMOJI_CATEGORIES[emojiTab]?.items || QUICK_EMOJIS)

    const canSend = (text.trim().length > 0 || pendingFile) && !uploading

    return (
        <div className='flex-col gap-6' style={{
            alignItems: 'stretch'
        }}>
            {replyTo && (
                <div className="chat-reply-bar">
                    <div className="flex-1 min-w-0">
                        <div className='text-xs fw-700 color-text'>
                            Ответ: {displayName(replyTo.sender_name) || 'пользователь'}
                        </div>
                        <div
                            style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            className='text-sm text-secondary overflow-hidden'>
                            {replyTo.content || replyTo.file_name || 'Вложение'}
                        </div>
                    </div>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelReply}>
                        <X size={14} />
                    </button>
                </div>
            )}
            {pendingFile && (
                <div className='text-sm text-secondary flex items-center gap-8'>
                    <span>📎 {pendingFile.filename}</span>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setPendingFile(null)}>
                        Убрать
                    </button>
                </div>
            )}
            {mentionOpen && filteredMentions.length > 0 && (
                <div
                    style={{
                        maxHeight: 160,
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-bg-elevated)'
                    }}
                    className='overflow-auto'>
                    {filteredMentions.map((u) => (
                        <button
                            key={u.id}
                            type="button"
                            className='w-full'
                            style={{
                                justifyContent: 'flex-start',
                                borderRadius: 0
                            }}
                            onClick={() => insertMention(u)}
                        >
                            <span className="fw-600">{displayName(u.full_name) || u.login}</span>
                            <span
                                style={{
                                    marginLeft: 8
                                }}
                                className='text-xs text-muted'>{u.login}</span>
                        </button>
                    ))}
                </div>
            )}
            {showEmoji && (
                <div className="chat-emoji-picker">
                    <div className="chat-emoji-picker__tabs">
                        {Object.entries(EMOJI_CATEGORIES).map(([key, def]) => (
                            <button
                                key={key}
                                type="button"
                                className={`chat-emoji-picker__tab ${emojiTab === key ? 'chat-emoji-picker__tab--active' : ''}`}
                                onClick={() => setEmojiTab(key)}
                            >
                                {def.label}
                            </button>
                        ))}
                    </div>
                    <div className="chat-emoji-picker__grid">
                        {emojiItems.map((ch) => (
                            <button
                                key={`${emojiTab}-${ch}`}
                                type="button"
                                className="chat-emoji-picker__item"
                                onClick={() => appendEmoji(ch)}
                            >
                                {ch}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {!liveSyncConnected && (
                <div
                    style={{
                        color: 'var(--color-warning)',
                        padding: '6px 8px',
                        borderRadius: 'var(--radius-md)',
                        background: 'var(--color-warning-bg)'
                    }}
                    className='text-xs fw-600 mb-6'>
                    Нет постоянного соединения (WebSocket). Сообщения отправляются через сервер; статусы «печатает» и мгновенная доставка могут не работать.
                </div>
            )}
            <div className='flex items-end gap-6'>
                <input className="hidden" ref={fileRef} type="file" onChange={onPickFile} />
                <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={disabled || uploading}
                    title="Вложение"
                    onClick={() => fileRef.current?.click()}
                >
                    <Paperclip size={18} />
                </button>
                <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={disabled}
                    title="Эмодзи"
                    onClick={() => setShowEmoji((s) => !s)}
                >
                    <Smile size={18} />
                </button>
                <textarea
                    ref={textareaRef}
                    className="chat-input__field flex-1"
                    placeholder="Написать сообщение… (@имя для упоминания)"
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    disabled={disabled || uploading}

                />
                <button
                    className="chat-input__send"
                    onClick={() => void handleSend()}
                    disabled={!canSend || disabled}
                    title="Отправить (Enter)"
                >
                    <Send size={16} />
                </button>
            </div>
        </div>
    );
}
